import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { Search, ExternalLink } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { KpiRow } from "@/components/app/KpiRow";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAllProfiles } from "@/hooks/use-admin-data";
import { useTeamEntries } from "@/hooks/use-team-entries";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { DEPARTMENT_CONFIGS } from "@/lib/department-configs";
import { modCanDept, modCanWorkspace, type ModeratorPermissions } from "@/lib/moderator-permissions";
import { TaskReviewModal, type TeamTaskMode } from "@/components/team/TaskReviewModal";
import {
  filterByDate, filterByStatus, searchRows, statusUiLabel, statusPillStyle, exportCsv,
  type DateFilter, type StatusFilter,
} from "@/components/team/team-entries";
import type { Submission } from "@/lib/submissions";
import { isCreativeReviewDept } from "@/lib/dept-helpers";
import { formatIst } from "@/lib/tz";

const ALL_DEPTS = Object.keys(DEPARTMENT_CONFIGS) as Department[];

export const Route = createFileRoute("/_app/_team/team/employees")({
  component: TeamEmployeesPage,
});

function TeamEmployeesPage() {
  const { profile, role } = useAuth();
  const perms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const isAdmin = role === "admin";
  const canSearch = isAdmin || modCanWorkspace(perms, "search_users");
  const ownDeptOnly =
    role === "moderator" && Boolean(perms.scope_own_department) && profile?.department;

  const { data: profiles = [] } = useAllProfiles();
  const { data: submissions = [] } = useTeamEntries();
  const profilesById = useMemo(() => new Map(profiles.map((p) => [p.id, p])), [profiles]);

  const visibleDepts = isAdmin
    ? ALL_DEPTS
    : ownDeptOnly
      ? [profile!.department as Department]
      : ALL_DEPTS.filter((d) => modCanDept(perms, d, "view"));

  const [deptFilter, setDeptFilter] = useState<"all" | Department>(
    ownDeptOnly ? (profile!.department as Department) : "all",
  );
  const [employeeId, setEmployeeId] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [search, setSearch] = useState("");
  const [modalEntry, setModalEntry] = useState<(Submission & { employee_name?: string }) | null>(null);
  const [modalMode, setModalMode] = useState<TeamTaskMode>("view");

  const employeeOptions = useMemo(() => {
    return profiles
      .filter((p) => isAdmin || visibleDepts.includes(p.department))
      .filter((p) => deptFilter === "all" || p.department === deptFilter);
  }, [profiles, visibleDepts, isAdmin, deptFilter]);

  const employee = employeeId !== "all" ? profilesById.get(employeeId) : null;

  const baseRows = useMemo(() => {
    let r = submissions.filter((s) => isAdmin || visibleDepts.includes(s.department));
    if (deptFilter !== "all") r = r.filter((s) => s.department === deptFilter);
    if (employeeId !== "all") r = r.filter((s) => s.user_id === employeeId);
    return r;
  }, [submissions, deptFilter, employeeId, visibleDepts, isAdmin]);

  const filtered = useMemo(() => {
    let r = filterByDate(baseRows, dateFilter);
    r = filterByStatus(r, statusFilter);
    r = searchRows(r, profilesById, search);
    return r;
  }, [baseRows, dateFilter, statusFilter, search, profilesById]);

  const kpi = {
    total: filtered.length,
    completed: filtered.filter((x) => x.status === "approved").length,
    pending: filtered.filter((x) => x.status === "pending").length,
    corrections: filtered.filter((x) => x.status === "correction_needed").length,
    overdue: filtered.filter((x) => statusUiLabel(x.status, x.submission_date).key === "overdue").length,
  };

  const lastUpdate = useMemo(() => {
    if (!baseRows.length) return null;
    return baseRows.reduce((acc, r) => acc && acc > r.created_at ? acc : r.created_at, "");
  }, [baseRows]);

  if (!canSearch) {
    return (
      <>
        <AppHeader subtitle="By Employee" />
        <div className="px-[22px] pb-6 text-[12px] text-muted-foreground">
          You don't have permission to view employees.
        </div>
      </>
    );
  }

  const canReview = isAdmin || role === "moderator";
  const headerLabel = employee
    ? `${DEPARTMENT_LABELS[employee.department].toUpperCase()} · ${employee.full_name.toUpperCase()}`
    : deptFilter === "all" ? "ALL EMPLOYEES" : DEPARTMENT_LABELS[deptFilter as Department].toUpperCase();

  return (
    <>
      <AppHeader subtitle="View all individual task updates by selected employee" />
      <div className="px-[22px] pb-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[280px] flex-1">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search task title, product, creative ID..." className="h-9 pl-7 text-[13px]" />
          </div>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="h-9 w-[180px] text-[12px]"><SelectValue placeholder="Employee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employeeOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={deptFilter}
            onValueChange={(v) => { setDeptFilter(v as "all" | Department); setEmployeeId("all"); }}
            disabled={!!ownDeptOnly}
          >
            <SelectTrigger className="h-9 w-[170px] text-[12px]"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              {!ownDeptOnly && <SelectItem value="all">All Departments</SelectItem>}
              {visibleDepts.map((d) => <SelectItem key={d} value={d}>{DEPARTMENT_LABELS[d]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="h-9 w-[120px] text-[12px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="review">Review</SelectItem>
              <SelectItem value="correction">Correction</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as DateFilter)}>
            <SelectTrigger className="h-9 w-[130px] text-[12px]"><SelectValue placeholder="Date" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button
            className="h-9 bg-[#7C3AED] text-white hover:bg-[#6D28D9]"
            onClick={() => exportCsv(`employee-${employee?.full_name ?? "all"}-${formatIst(new Date(), "yyyyMMdd")}.csv`, filtered, profilesById)}
          >
            Export
          </Button>
        </div>

        <KpiRow
          items={[
            { label: "Total Entries", value: String(kpi.total), trend: employee ? `${employee.full_name} this month` : "this month", variant: "primary" },
            { label: "Completed", value: String(kpi.completed), trend: "approved entries" },
            { label: "Pending Review", value: String(kpi.pending), trend: "awaiting feedback" },
            { label: "Corrections", value: String(kpi.corrections), variant: "danger", trend: "needs changes" },
            { label: "Overdue", value: String(kpi.overdue), variant: "danger", trend: "late submission" },
          ]}
        />

        <div className="mt-2 grid gap-4 lg:grid-cols-[1fr_280px]">
          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">{headerLabel}</div>
            <h2 className="mb-3 text-[18px] font-bold">Single Task Entries</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-border bg-muted/40 text-[10px] uppercase tracking-[0.4px] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Task Title</th>
                    <th className="px-3 py-2 text-left font-semibold">ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Product</th>
                    <th className="px-3 py-2 text-left font-semibold">Brand</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-left font-semibold">Link</th>
                    <th className="px-3 py-2 text-left font-semibold">Correction</th>
                    <th className="px-3 py-2 text-left font-semibold">Reviewer</th>
                    <th className="px-3 py-2 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">No task entries found for this filter.</td></tr>
                  )}
                  {filtered.map((r) => {
                    const p = profilesById.get(r.user_id);
                    const reviewer = r.reviewed_by ? profilesById.get(r.reviewed_by) : null;
                    const det = (r.details ?? {}) as Record<string, unknown>;
                    const link = (det.link as string) ?? (det.drive_link as string) ?? (det.creative_drive_link as string);
                    const idVal = (det.creative_id as string) ?? (det.task_id as string) ?? "—";
                    const ui = statusUiLabel(r.status, r.submission_date);
                    const pill = statusPillStyle(ui.key);
                    return (
                      <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/30" style={{ height: 48 }}>
                        <td className="px-3 py-2">{format(parseISO(r.submission_date), "dd MMM yyyy")}</td>
                        <td className="px-3 py-2 font-medium">{r.title}</td>
                        <td className="px-3 py-2">{idVal}</td>
                        <td className="px-3 py-2">{r.product ?? "—"}</td>
                        <td className="px-3 py-2">{r.brand ?? "—"}</td>
                        <td className="px-3 py-2">
                          <span style={{ background: pill.bg, color: pill.fg, padding: "2px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                            {ui.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {link ? (
                            <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">
                              {link.length > 18 ? link.slice(0, 18) + "…" : link}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2">{isCreativeReviewDept(r.department) ? (r.correction_notes ? "Note" : "None") : "—"}</td>
                        <td className="px-3 py-2">{isCreativeReviewDept(r.department) ? (reviewer?.full_name ?? "—") : "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => { setModalEntry({ ...r, employee_name: p?.full_name }); setModalMode("view"); }}>View</Button>
                            {isCreativeReviewDept(r.department) && canReview && (
                              <Button size="sm" className="h-7 bg-[#18181B] px-2 text-[11px] text-white hover:bg-black" onClick={() => { setModalEntry({ ...r, employee_name: p?.full_name }); setModalMode("review"); }}>Review</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">Summary</div>
            <h2 className="text-[18px] font-bold">Employee Summary</h2>
            <div className="mt-3 space-y-2 text-[12px]">
              <SummaryRow k="Name" v={employee?.full_name ?? "All employees"} />
              <SummaryRow k="Department" v={employee ? DEPARTMENT_LABELS[employee.department] : (deptFilter === "all" ? "All" : DEPARTMENT_LABELS[deptFilter as Department])} />
              <SummaryRow k="Active Tasks" v={String(filtered.filter((r) => r.status !== "approved" && r.status !== "rejected").length)} />
              <SummaryRow k="Last Update" v={lastUpdate ? format(parseISO(lastUpdate), "dd MMM yyyy · HH:mm") : "—"} />
              <SummaryRow k="Pending" v={String(kpi.pending)} />
              <SummaryRow k="Corrections" v={String(kpi.corrections)} />
              <SummaryRow k="Completed" v={String(kpi.completed)} />
            </div>
          </div>
        </div>
      </div>

      <TaskReviewModal
        open={!!modalEntry}
        onClose={() => setModalEntry(null)}
        mode={modalMode}
        entry={modalEntry}
      />
    </>
  );
}

function SummaryRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 pb-1.5 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-semibold">{v}</span>
    </div>
  );
}
